// @ts-nocheck
// Neo Price Range Component - Canvas Version
// Usage: <neo-price-range min="1000" max="10000" min-value="2500" max-value="7500"></neo-price-range>

// Global state manager for syncing all instances
class PriceRangeState {
  constructor() {
    this.listeners = new Set();
    this.state = {
      minValue: 0,
      maxValue: 100,
      minPrice: 0,
      maxPrice: 0,
    };
  }

  subscribe(callback) {
    this.listeners.add(callback);
  }

  unsubscribe(callback) {
    this.listeners.delete(callback);
  }

  setState(newState) {
    this.state = { ...this.state, ...newState };
    this.listeners.forEach((callback) => callback(this.state));
  }

  getState() {
    return this.state;
  }
}

const globalPriceRangeState = new PriceRangeState();

// Indian price formatting helper
function formatIndianPrice(value) {
  if (value == null || isNaN(value)) return '0';
  const sign = value < 0 ? '-' : '';
  const str = String(Math.floor(Math.abs(value)));
  if (str.length <= 3) return sign + str;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  const restWithCommas = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return sign + restWithCommas + ',' + last3;
}

class NeoPriceRange extends HTMLElement {
  static get observedAttributes() {
    // BUG 1 FIX: observe 'min-value' and 'max-value' only (not 'initial-min'/'initial-max').
    // The in-grid section was using 'initial-min'/'initial-max' which were never observed,
    // so they silently did nothing — fixed by standardising on 'min-value'/'max-value' everywhere.
    return ['min', 'max', 'min-value', 'max-value'];
  }

  constructor() {
    super();
    this.minPrice = 0;
    this.maxPrice = 0;
    this.canvas = null;
    this.ctx = null;
    this.lines = [];
    this.pendingUpdate = null;
    this.pendingCanvasDraw = null;
    this.isInitialized = false;
    // Tracks which handle is currently stacked on top so we only write
    // style.zIndex (and trigger a style recalc) when the order actually flips.
    this._minOnTop = null;
    // BUG 3 FIX: track whether this instance initiated the current state change
    // so we don't overwrite an active user selection when a second instance mounts.
    this._suppressStateSync = false;

    this.handleMinInput = this.handleMinInput.bind(this);
    this.handleMaxInput = this.handleMaxInput.bind(this);
    this.handleMinChange = this.handleMinChange.bind(this);
    this.handleMaxChange = this.handleMaxChange.bind(this);
    this.handleTextMinBlur = this.handleTextMinBlur.bind(this);
    this.handleTextMaxBlur = this.handleTextMaxBlur.bind(this);
    this.handleTextMinKeypress = this.handleTextMinKeypress.bind(this);
    this.handleTextMaxKeypress = this.handleTextMaxKeypress.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isInitialized) return;
    if (oldValue === newValue) return;

    if (name === 'min' || name === 'max') {
      this.minPrice = parseInt(this.getAttribute('min'));
      this.maxPrice = parseInt(this.getAttribute('max'));

      globalPriceRangeState.setState({
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
      });

      const state = globalPriceRangeState.getState();
      const minPrice = Math.round(this.minPrice + (state.minValue / 100) * (this.maxPrice - this.minPrice));
      const maxPrice = Math.round(this.minPrice + (state.maxValue / 100) * (this.maxPrice - this.minPrice));

      this.minLabel.textContent = `₹ ${formatIndianPrice(minPrice)}`;
      this.maxLabel.textContent = `₹ ${formatIndianPrice(maxPrice)}`;
      this.minTextInput.value = formatIndianPrice(minPrice);
      this.maxTextInput.value = formatIndianPrice(maxPrice);

      this.updateCanvas(state.minValue, state.maxValue);
    }

    if (name === 'min-value' || name === 'max-value') {
      const minValuePrice = parseInt(this.getAttribute('min-value'));
      const maxValuePrice = parseInt(this.getAttribute('max-value'));

      const initialMinPrice = !isNaN(minValuePrice) ? minValuePrice : this.minPrice;
      const initialMaxPrice = !isNaN(maxValuePrice) ? maxValuePrice : this.maxPrice;

      const initialMinPercent = ((initialMinPrice - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
      const initialMaxPercent = ((initialMaxPrice - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;

      // BUG 3 FIX: suppress the state broadcast when setting values from attributes
      // so that re-rendering filters (after applyFilters) doesn't reset the user's
      // actively-selected price range back to the filter's initial values.
      this._suppressStateSync = true;
      globalPriceRangeState.setState({
        minValue: Math.max(0, Math.min(100, initialMinPercent)),
        maxValue: Math.max(0, Math.min(100, initialMaxPercent)),
      });
      this._suppressStateSync = false;
    }
  }

  connectedCallback() {
    this.minPrice = parseInt(this.getAttribute('min'));
    this.maxPrice = parseInt(this.getAttribute('max'));

    const minValuePrice = parseInt(this.getAttribute('min-value'));
    const maxValuePrice = parseInt(this.getAttribute('max-value'));

    const initialMinPrice = !isNaN(minValuePrice) ? minValuePrice : this.minPrice;
    const initialMaxPrice = !isNaN(maxValuePrice) ? maxValuePrice : this.maxPrice;

    const initialMinPercent = ((initialMinPrice - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
    const initialMaxPercent = ((initialMaxPrice - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;

    const clampedMinPercent = Math.max(0, Math.min(100, initialMinPercent));
    const clampedMaxPercent = Math.max(0, Math.min(100, initialMaxPercent));

    // BUG 3 & 5 FIX: only push our initial values into global state if no other
    // instance has already established a user selection (i.e. minValue/maxValue
    // are still at defaults 0/100). This prevents a second instance (in-grid price
    // section) from overwriting the sidebar slider's active selection on mount.
    const existing = globalPriceRangeState.getState();
    const isDefaultState = existing.minValue === 0 && existing.maxValue === 100;

    if (isDefaultState) {
      globalPriceRangeState.setState({
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
        minValue: clampedMinPercent,
        maxValue: clampedMaxPercent,
      });
    } else {
      // Sync price bounds but keep existing selection percentages
      globalPriceRangeState.setState({
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
      });
    }

    // Render using whatever the current global state says (may differ from our attrs
    // if another instance already established a selection)
    const currentState = globalPriceRangeState.getState();
    const renderMinPrice = Math.round(this.minPrice + (currentState.minValue / 100) * (this.maxPrice - this.minPrice));
    const renderMaxPrice = Math.round(this.minPrice + (currentState.maxValue / 100) * (this.maxPrice - this.minPrice));
    const renderMinPercent = currentState.minValue;
    const renderMaxPercent = currentState.maxValue;

    this.innerHTML = `
      <div class="neo-price-range">
        <canvas class="neo-price-range__canvas"></canvas>
        <div class="neo-price-range__track">
            <div class="neo-price-range__center-line"></div>
            <div class="neo-price-range__inputs">
                <input type="range" class="neo-price-range__input neo-price-range__input--min" min="0" max="100" step="0.1" value="${renderMinPercent}">
                <input type="range" class="neo-price-range__input neo-price-range__input--max" min="0" max="100" step="0.1" value="${renderMaxPercent}">
            </div>
        </div>
        <div class="neo-price-range__labels">
          <span class="neo-price-range__label neo-price-range__label--min">₹ ${formatIndianPrice(renderMinPrice)}</span>
          <span class="neo-price-range__label neo-price-range__label--max">₹ ${formatIndianPrice(renderMaxPrice)}</span>
        </div>
        <div class="neo-price-range__text-inputs">
          <div class="neo-price-range__text-input-wrapper">
            <span class="neo-price-range__currency">₹</span>
            <input type="text" class="neo-price-range__text-input neo-price-range__text-input--min" value="${formatIndianPrice(renderMinPrice)}">
          </div>
          <span class="neo-price-range__separator">-</span>
          <div class="neo-price-range__text-input-wrapper">
            <span class="neo-price-range__currency">₹</span>
            <input type="text" class="neo-price-range__text-input neo-price-range__text-input--max" value="${formatIndianPrice(renderMaxPrice)}">
          </div>
        </div>
        <div class="neo-price-range__error neo-price-range__error--hidden"></div>
      </div>
    `;

    this.canvas = this.querySelector('.neo-price-range__canvas');
    this.minInput = this.querySelector('.neo-price-range__input--min');
    this.maxInput = this.querySelector('.neo-price-range__input--max');
    this.minLabel = this.querySelector('.neo-price-range__label--min');
    this.maxLabel = this.querySelector('.neo-price-range__label--max');
    this.minTextInput = this.querySelector('.neo-price-range__text-input--min');
    this.maxTextInput = this.querySelector('.neo-price-range__text-input--max');
    this.errorElement = this.querySelector('.neo-price-range__error');

    requestAnimationFrame(() => {
      this.initCanvas();
    })
    this.createLineData();
    this.setupEventListeners();

    globalPriceRangeState.subscribe(this.handleStateChange);

    this.updateCanvas(renderMinPercent, renderMaxPercent);

    window.addEventListener('resize', this.handleResize);

    this.isInitialized = true;
  }

  disconnectedCallback() {
    globalPriceRangeState.unsubscribe(this.handleStateChange);
    this.minInput.removeEventListener('input', this.handleMinInput);
    this.maxInput.removeEventListener('input', this.handleMaxInput);
    this.minInput.removeEventListener('change', this.handleMinChange);
    this.maxInput.removeEventListener('change', this.handleMaxChange);
    this.minTextInput.removeEventListener('blur', this.handleTextMinBlur);
    this.maxTextInput.removeEventListener('blur', this.handleTextMaxBlur);
    this.minTextInput.removeEventListener('keypress', this.handleTextMinKeypress);
    this.maxTextInput.removeEventListener('keypress', this.handleTextMaxKeypress);
    window.removeEventListener('resize', this.handleResize);

    if (this.pendingUpdate) cancelAnimationFrame(this.pendingUpdate);
    if (this.pendingCanvasDraw) cancelAnimationFrame(this.pendingCanvasDraw);
  }

  initCanvas() {
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    this.resizeCanvas();
  }

  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
    this.canvasWidth = rect.width;
    this.canvasHeight = rect.height;
  }

  handleResize() {
    this.resizeCanvas();
    const state = globalPriceRangeState.getState();
    this.updateCanvas(state.minValue, state.maxValue);
  }

  createLineData() {
    this.lines = [];
    const totalLines = 80;
    for (let i = 0; i < totalLines; i++) {
      const positionInPattern = i % 5;
      const isTall = positionInPattern === 0 || positionInPattern === 1;
      const baseHeight = isTall ? 18.7 : 8.9;
      this.lines.push({ isTall, baseHeight, isActive: false });
    }
  }

  setupEventListeners() {
    this.minInput.addEventListener('input', this.handleMinInput);
    this.maxInput.addEventListener('input', this.handleMaxInput);
    this.minInput.addEventListener('change', this.handleMinChange);
    this.maxInput.addEventListener('change', this.handleMaxChange);
    this.minTextInput.addEventListener('blur', this.handleTextMinBlur);
    this.maxTextInput.addEventListener('blur', this.handleTextMaxBlur);
    this.minTextInput.addEventListener('keypress', this.handleTextMinKeypress);
    this.maxTextInput.addEventListener('keypress', this.handleTextMaxKeypress);
  }

  // Only touches style.zIndex when the stacking order actually needs to flip,
  // instead of writing it unconditionally on every input/state tick.
  _setHandleStacking(minOnTop) {
    if (this._minOnTop === minOnTop) return;
    this._minOnTop = minOnTop;
    this.minInput.style.zIndex = minOnTop ? 5 : 3;
    this.maxInput.style.zIndex = minOnTop ? 3 : 5;
  }

  handleMinInput(e) {
    let value = parseFloat(e.target.value);
    const maxValue = parseFloat(this.maxInput.value);
    this._setHandleStacking(value > 50);

    if (value >= maxValue - 1) {
      value = maxValue - 1;
      e.target.value = value;
    }

    if (this.pendingUpdate) cancelAnimationFrame(this.pendingUpdate);
    this.pendingUpdate = requestAnimationFrame(() => {
      globalPriceRangeState.setState({ minValue: value });
      this.pendingUpdate = null;
    });
  }

  handleMaxInput(e) {
    let value = parseFloat(e.target.value);
    const minValue = parseFloat(this.minInput.value);
    this._setHandleStacking(value < 50);

    if (value <= minValue + 1) {
      value = minValue + 1;
      e.target.value = value;
    }

    if (this.pendingUpdate) cancelAnimationFrame(this.pendingUpdate);
    this.pendingUpdate = requestAnimationFrame(() => {
      globalPriceRangeState.setState({ maxValue: value });
      this.pendingUpdate = null;
    });
  }

  handleMinChange() {
    this.emitPriceRangeChange();
  }

  handleMaxChange() {
    this.emitPriceRangeChange();
  }

  emitPriceRangeChange() {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    const state = globalPriceRangeState.getState();
    const { minValue, maxValue, minPrice, maxPrice } = state;

    if (
      typeof minValue !== 'number' ||
      typeof maxValue !== 'number' ||
      typeof minPrice !== 'number' ||
      typeof maxPrice !== 'number' ||
      maxPrice <= minPrice
    ) return;

    const currentMinPrice = Math.round(minPrice + (minValue / 100) * (maxPrice - minPrice));
    const currentMaxPrice = Math.round(minPrice + (maxValue / 100) * (maxPrice - minPrice));

    document.dispatchEvent(
      new CustomEvent('neo:price-range-change', {
        detail: {
          minPercent: minValue,
          maxPercent: maxValue,
          minPrice: currentMinPrice,
          maxPrice: currentMaxPrice,
        },
      }),
    );
  }

  handleTextMinBlur(e) {
    this.validateAndUpdateMin(e.target.value);
  }

  handleTextMinKeypress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value;
      setTimeout(() => {
        this.validateAndUpdateMin(value);
        this.minTextInput.blur();
      }, 0);
    }
  }

  handleTextMaxBlur(e) {
    this.validateAndUpdateMax(e.target.value);
  }

  handleTextMaxKeypress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value;
      setTimeout(() => {
        this.validateAndUpdateMax(value);
        this.maxTextInput.blur();
      }, 0);
    }
  }

  validateAndUpdateMin(inputValue) {
    // BUG 6 FIX: strip everything except digits — this already removes '-'.
    // The old code was correct here but the empty-string early-return masked
    // negative inputs: "-500" → numericValue="500" which passed. The real
    // problem was that a pure "-" input → "" → early return with no error.
    // We now explicitly check for empty after stripping and reset cleanly.
    const numericValue = String(inputValue).replace(/[^0-9]/g, '');

    if (numericValue === '') {
      const state = globalPriceRangeState.getState();
      const minPrice = Math.round(this.minPrice + (state.minValue / 100) * (this.maxPrice - this.minPrice));
      this.minTextInput.value = formatIndianPrice(minPrice);
      this.clearError();
      return;
    }

    const value = parseInt(numericValue, 10);

    if (value < this.minPrice) {
      this.showError(`Minimum value is ₹ ${formatIndianPrice(this.minPrice)}`);
      this.minTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        this.minTextInput.value = formatIndianPrice(this.minPrice);
        this.clearError();
        this.minTextInput.classList.remove('neo-price-range__text-input--error');
        // Also snap the slider/state to minPrice
        const percentage = 0;
        globalPriceRangeState.setState({ minValue: percentage });
        this.emitPriceRangeChange();
      }, 2000);
      return;
    }

    if (value > this.maxPrice) {
      this.showError(`Maximum value is ₹ ${formatIndianPrice(this.maxPrice)}`);
      this.minTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        this.minTextInput.value = formatIndianPrice(this.maxPrice);
        this.clearError();
        this.minTextInput.classList.remove('neo-price-range__text-input--error');
      }, 2000);
      return;
    }

    // BUG 6 FIX: read currentMaxValue from global state (converted back to price),
    // not from the text input — the text input may still show a formatted/partial
    // value and parseInt on it can return NaN or a wrong number when commas are present.
    const globalState = globalPriceRangeState.getState();
    const currentMaxValue = Math.round(this.minPrice + (globalState.maxValue / 100) * (this.maxPrice - this.minPrice));

    if (value >= currentMaxValue) {
      this.showError(`Minimum value must be less than ₹ ${formatIndianPrice(currentMaxValue)}`);
      this.minTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        const corrected = currentMaxValue - 100;
        this.minTextInput.value = formatIndianPrice(corrected);
        this.clearError();
        this.minTextInput.classList.remove('neo-price-range__text-input--error');
        const percentage = ((corrected - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
        globalPriceRangeState.setState({ minValue: Math.max(0, percentage) });
        this.emitPriceRangeChange();
      }, 2000);
      return;
    }

    this.clearError();
    this.minTextInput.classList.remove('neo-price-range__text-input--error');

    const percentage = ((value - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
    globalPriceRangeState.setState({ minValue: percentage });
    this.emitPriceRangeChange();
  }

  validateAndUpdateMax(inputValue) {
    // BUG 6 FIX: same as validateAndUpdateMin — strip to digits only, no sign chars.
    const numericValue = String(inputValue).replace(/[^0-9]/g, '');

    if (numericValue === '') {
      const state = globalPriceRangeState.getState();
      const maxPrice = Math.round(this.minPrice + (state.maxValue / 100) * (this.maxPrice - this.minPrice));
      this.maxTextInput.value = formatIndianPrice(maxPrice);
      this.clearError();
      return;
    }

    const value = parseInt(numericValue, 10);

    if (value < this.minPrice) {
      this.showError(`Minimum value is ₹ ${formatIndianPrice(this.minPrice)}`);
      this.maxTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        this.maxTextInput.value = formatIndianPrice(this.minPrice);
        this.clearError();
        this.maxTextInput.classList.remove('neo-price-range__text-input--error');
      }, 2000);
      return;
    }

    if (value > this.maxPrice) {
      this.showError(`Maximum value is ₹ ${formatIndianPrice(this.maxPrice)}`);
      this.maxTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        this.maxTextInput.value = formatIndianPrice(this.maxPrice);
        this.clearError();
        this.maxTextInput.classList.remove('neo-price-range__text-input--error');
        // Snap state to maxPrice
        globalPriceRangeState.setState({ maxValue: 100 });
        this.emitPriceRangeChange();
      }, 2000);
      return;
    }

    // BUG 6 FIX: read currentMinValue from global state, not the text input.
    const globalState = globalPriceRangeState.getState();
    const currentMinValue = Math.round(this.minPrice + (globalState.minValue / 100) * (this.maxPrice - this.minPrice));

    if (value <= currentMinValue) {
      this.showError(`Maximum value must be greater than ₹ ${formatIndianPrice(currentMinValue)}`);
      this.maxTextInput.classList.add('neo-price-range__text-input--error');
      setTimeout(() => {
        const corrected = currentMinValue + 100;
        this.maxTextInput.value = formatIndianPrice(corrected);
        this.clearError();
        this.maxTextInput.classList.remove('neo-price-range__text-input--error');
        const percentage = ((corrected - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
        globalPriceRangeState.setState({ maxValue: Math.min(100, percentage) });
        this.emitPriceRangeChange();
      }, 2000);
      return;
    }

    this.clearError();
    this.maxTextInput.classList.remove('neo-price-range__text-input--error');

    const percentage = ((value - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
    globalPriceRangeState.setState({ maxValue: percentage });
    this.emitPriceRangeChange();
  }

  showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.classList.remove('neo-price-range__error--hidden');
  }

  clearError() {
    this.errorElement.textContent = '';
    this.errorElement.classList.add('neo-price-range__error--hidden');
  }

  handleStateChange(state) {
    // BUG 3 FIX: don't respond to state changes that we ourselves triggered via
    // attribute sync (e.g. when renderFilters re-mounts this element with fresh
    // min-value/max-value attrs). Without this guard, the state change would
    // bounce back and trigger updateFromState, potentially fighting the user's input.
    if (this._suppressStateSync) return;
    this.updateFromState(state);
  }

  updateFromState(state) {
    const { minValue, maxValue } = state;

    this.minInput.value = minValue;
    this.maxInput.value = maxValue;

    this._setHandleStacking(minValue > 50);

    const minPrice = Math.round(this.minPrice + (minValue / 100) * (this.maxPrice - this.minPrice));
    const maxPrice = Math.round(this.minPrice + (maxValue / 100) * (this.maxPrice - this.minPrice));

    this.minLabel.textContent = `₹  ${formatIndianPrice(minPrice)}`;
    this.maxLabel.textContent = `₹  ${formatIndianPrice(maxPrice)}`;

    if (document.activeElement !== this.minTextInput) {
      this.minTextInput.value = formatIndianPrice(minPrice);
    }
    if (document.activeElement !== this.maxTextInput) {
      this.maxTextInput.value = formatIndianPrice(maxPrice);
    }

    this.updateCanvas(minValue, maxValue);
  }

  updateCanvas(minPercent, maxPercent) {
    const minIndex = Math.floor((minPercent / 100) * this.lines.length);
    const maxIndex = Math.ceil((maxPercent / 100) * this.lines.length);

    this.lines.forEach((line, index) => {
      line.isActive = index >= minIndex && index <= maxIndex;
    });

    this.drawCanvas();
  }

  drawCanvas() {
    if (this.pendingCanvasDraw) return;

    this.pendingCanvasDraw = requestAnimationFrame(() => {
      const ctx = this.ctx;
      const width = this.canvasWidth;
      const height = this.canvasHeight;

      ctx.clearRect(0, 0, width, height);

      const totalLines = this.lines.length;
      const gap = 1.5;
      const lineWidth = 1;
      const totalGaps = (totalLines - 1) * gap;
      const availableWidth = width - totalGaps;
      const spacing = availableWidth / totalLines;

      this.lines.forEach((line, index) => {
        const x = index * (spacing + gap) + spacing / 2;

        let lineHeight = line.baseHeight;
        if (line.isActive) {
          const maxHeight = 55;
          const randomScale = 1 + Math.random() * (maxHeight / line.baseHeight - 1);
          lineHeight = line.baseHeight * randomScale;
        }

        const y = height - lineHeight;
        ctx.fillStyle = line.isActive ? '#ee3224' : '#ffffff';
        ctx.fillRect(x - lineWidth / 2, y, lineWidth, lineHeight);
      });

      this.pendingCanvasDraw = null;
    });
  }
}

customElements.define('neo-price-range', NeoPriceRange);
